import { ApolloClient, InMemoryCache, gql, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { getEffectiveLimit, isMaxRowsEnabled, getMaxRows, getPageSize, perfLog } from '../config/appConfig';

// Updated to use Vite's import.meta.env for environment variables

// GraphQL endpoint configuration
import { getApiBaseUrl } from '../config/appConfig';
const GRAPHQL_ENDPOINT = `${getApiBaseUrl()}/graphql`;

console.log('Using GraphQL Endpoint:', GRAPHQL_ENDPOINT);

// Create HTTP link to the GraphQL API
const httpLink = createHttpLink({
  uri: GRAPHQL_ENDPOINT,
});

// Auth link with bearer token authentication
const authLink = setContext((_, { headers }) => {
  return {
    headers: {
      ...headers,
      'Content-Type': 'application/json',
      // 'Authorization': `Bearer ${token}` // Add token logic if required
    }
  };
});

// Apollo Client instance with error handling and caching
const client = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache({
    // Configure cache policies if needed
    typePolicies: {
      Query: {
        fields: {
          // Add field policies for caching strategies
        }
      }
    }
  }),
  // Enable network error logging
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
    },
    query: {
      errorPolicy: 'all',
    },
  },
});

// Common GraphQL queries
export const QUERIES = {
  // Example introspection query to test connection
  INTROSPECTION: gql`
    query IntrospectionQuery {
      __schema {
        types {
          name
          kind
          description
        }
      }
    }
  `,
  
  // Placeholder query - replace with actual schema fields when available
  GET_ENERGY_DATA: gql`
    query GetEnergyData {
      __typename
    }
  `,

  // Product Data query with pagination support
  PRODUCT_DATA: gql`
    query ProductData($product: [ProductSectorInput!], $country: [String!], $subregion: [SubregionInput!], $activity: [String!], $continuationToken: String, $limit: Int) {
      productData(product: $product, country: $country, subregion: $subregion, activity: $activity, continuationToken: $continuationToken, limit: $limit) {
        metaData {
          totalCount
          requestCharge
          hasMoreResults
          continuationToken
        }
        data {
          country
          subregion
          activity
          sector
          product
          year
          month
          value
          status
        }
      }
    }
  `,
  // Product Counts query
  PRODUCT_COUNTS: gql`
    query countByProductSector {
      countByProductSector {
        product
        sector
        count
      }
    }
  `,

  // Country counts query
  COUNTRY_COUNTS: gql`
    query CountByCountry {
      countByCountry {
        country
        count
      }
    }
  `,

  // Activity counts query
  ACTIVITY_COUNTS: gql`
    query CountByActivity {
      countByActivity {
        activity
        count
      }
    }
  `,

  // Filename counts
  FILENAME_COUNTS: gql`
    query CountByFilename {
      countByFilename {
        fileName
        recordCount
      }
    }
  `,
}

// Helper functions for common operations
export const graphqlService = {
  // Test connection to GraphQL API
  async testConnection() {
    try {
      const result = await client.query({
        query: QUERIES.INTROSPECTION,
        fetchPolicy: 'network-only',
      });
      
      return {
        success: true,
        data: result.data,
        types: result.data.__schema?.types?.length || 0,
      };
    } catch (error) {
      console.error('GraphQL connection test failed:', error);
      return {
        success: false,
        error: error.message,
        networkError: error.networkError?.message,
        graphQLErrors: error.graphQLErrors,
      };
    }
  },

  // Execute custom query with error handling
  async executeQuery(query, variables = {}) {
    console.log('Executing GraphQL Query with variables:', variables, query);
    
    try {
      const result = await client.query({
        query,
        variables,
        fetchPolicy: 'cache-first',
      });

      console.log('GraphQL query result:', result);

      return {
        success: true,
        data: result.data,
        loading: result.loading,
      };
    } catch (error) {
      console.error('GraphQL query failed:', error);
      return {
        success: false,
        error: error.message,
        networkError: error.networkError?.message,
        graphQLErrors: error.graphQLErrors,
      };
    }
  },

  // Execute query with automatic pagination using continuationToken
  async executeQueryWithPagination(query, variables = {}, options = {}, token) {
    console.log('Executing GraphQL Query with variables:', variables, query);
    const {
      maxPages = 100,
      onPageReceived = null,
      mergePagesData = true,
      fetchPolicy = 'cache-first'
    } = options;

    let allData = [];
    let allMetaData = null;
    let currentPage = 0;
    let continuationToken = variables.continuationToken || null;
    let hasMoreResults = true;

    try {
      while (hasMoreResults && currentPage < maxPages) {
        perfLog(`Fetching page ${currentPage + 1}${continuationToken ? ` with token: ${continuationToken.substring(0, 20)}...` : ''}`);
        
        // Execute query with current continuation token
        const currentVariables = {
          ...variables,
          continuationToken
        };

        console.log('Current Variables for page', currentPage + 1, ':', currentVariables);

        const result = await client.query({
          query,
          variables: currentVariables,
          fetchPolicy: currentPage === 0 ? fetchPolicy : 'network-only', // First page can use cache, subsequent pages should be fresh
          context: {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        });

        console.log('GraphQL query result:', result);

        if (!result.data) {
          throw new Error('No data received from GraphQL query');
        }

        // Extract data and metadata from the result
        // Assumes the structure follows the pattern seen in PRODUCT_DATA query
        const responseData = this.extractPaginatedData(result.data);
        
        if (!responseData) {
          console.warn('Could not extract paginated data from response:', result.data);
          break;
        }

        const { data, metaData } = responseData;
        
        // Store metadata from the first page or update with latest
        if (currentPage === 0 || !allMetaData) {
          allMetaData = { ...metaData };
        } else {
          // Update cumulative metadata
          allMetaData.totalCount = (allMetaData.totalCount || 0) + (metaData.totalCount || 0);
          allMetaData.requestCharge = (allMetaData.requestCharge || 0) + (metaData.requestCharge || 0);
        }

        // Add current page data
        if (data && Array.isArray(data)) {
          allData = allData.concat(data);
          perfLog(`Page ${currentPage + 1}: Received ${data.length} items. Total so far: ${allData.length}`);
        }

        // Call page received callback if provided
        if (onPageReceived) {
          await onPageReceived({
            pageNumber: currentPage + 1,
            pageData: data,
            totalItemsSoFar: allData.length,
            metaData: metaData
          });
        }

        // Check for continuation
        continuationToken = metaData?.continuationToken;
        hasMoreResults = metaData?.hasMoreResults && !!continuationToken;
        
        console.log(`Page ${currentPage + 1} completed:`);
        console.log(`  - Items in this page: ${data?.length || 0}`);
        console.log(`  - Total items so far: ${allData.length}`);
        console.log(`  - Has more results: ${metaData?.hasMoreResults}`);
        console.log(`  - Continuation token present: ${!!continuationToken}`);
        console.log(`  - Will continue: ${hasMoreResults}`);
        
        currentPage++;

        // Safety check
        if (currentPage >= maxPages) {
          console.warn(`⚠️ Reached maximum pages limit (${maxPages}). Stopping pagination.`);
          console.warn(`   Total items retrieved: ${allData.length}`);
          console.warn(`   Has more data available: ${hasMoreResults}`);
          break;
        }
        
        // If we have more results but no continuation token, that's an error
        if (metaData?.hasMoreResults && !continuationToken) {
          console.error(`❌ API indicates more results available but no continuation token provided!`);
          console.error(`   This might be an API issue. Stopping pagination.`);
          break;
        }
      }

      // Update final metadata
      if (allMetaData) {
        allMetaData.hasMoreResults = hasMoreResults;
        allMetaData.continuationToken = hasMoreResults ? continuationToken : null;
        allMetaData.pagesRetrieved = currentPage;
      }

      return {
        success: true,
        data: mergePagesData ? this.buildPaginatedResponse(allData, allMetaData) : allData,
        totalPages: currentPage,
        totalItems: allData.length,
        hasMoreResults,
        metaData: allMetaData
      };

    } catch (error) {
      console.error('GraphQL paginated query failed:', error);
      return {
        success: false,
        error: error.message,
        networkError: error.networkError?.message,
        graphQLErrors: error.graphQLErrors,
        partialData: allData.length > 0 ? allData : null,
        totalPages: currentPage,
        totalItems: allData.length
      };
    }
  },

  // Helper function to extract data and metadata from various query response structures
  extractPaginatedData(responseData) {
    // Handle productData query structure
    if (responseData.productData) {
      return {
        data: responseData.productData.data,
        metaData: responseData.productData.metaData
      };
    }
    
    // Handle other potential structures - add more as needed
    // Look for common patterns: data + metaData, items + pageInfo, etc.
    const keys = Object.keys(responseData);
    for (const key of keys) {
      const value = responseData[key];
      if (value && typeof value === 'object') {
        // Check if this object has both data and metaData properties
        if (value.data && value.metaData) {
          return {
            data: value.data,
            metaData: value.metaData
          };
        }
        
        // Check for other pagination patterns
        if (value.items && value.pageInfo) {
          return {
            data: value.items,
            metaData: {
              totalCount: value.pageInfo.totalCount,
              hasMoreResults: value.pageInfo.hasNextPage,
              continuationToken: value.pageInfo.endCursor,
              requestCharge: value.pageInfo.requestCharge
            }
          };
        }
      }
    }
    
    // If no recognized pattern, return null
    return null;
  },

  // Helper function to build the final paginated response in the expected format
  buildPaginatedResponse(allData, metaData) {
    return {
      productData: {
        metaData: {
          ...metaData,
          totalCount: allData.length
        },
        data: allData
      }
    };
  },

  // Execute mutation with error handling
  async executeMutation(mutation, variables = {}) {
    try {
      const result = await client.mutate({
        mutation,
        variables,
      });

      return {
        success: true,
        data: result.data,
      };
    } catch (error) {
      console.error('GraphQL mutation failed:', error);
      return {
        success: false,
        error: error.message,
        networkError: error.networkError?.message,
        graphQLErrors: error.graphQLErrors,
      };
    }
  },

  // Clear cache
  clearCache() {
    return client.clearStore();
  },

  // Get cache size for monitoring
  getCacheSize() {
    return client.cache.extract();
  },

  // Convenience method to fetch all product data with pagination
  async getAllProductData(variables = {}, options = {}) {
    perfLog('Fetching product data with pagination...');
    console.log('Fetching Variables:', variables);
    // Use configured page size for individual requests
    let queryLimit = getPageSize();  // This will be 5000 in dev, 10000 in prod
    let maxPages = options.maxPages || 50;
    
    if (isMaxRowsEnabled()) {
      const maxRows = getMaxRows();  // This will be 60000 in dev, 500000 in prod
      
      // Override maxPages based on total records desired vs page size
      maxPages = Math.ceil(maxRows / queryLimit);
      
      perfLog(`🔧 Pagination config: ${maxRows} total records, ${queryLimit} per page, max ${maxPages} pages`);
    }
    
    // Parse product into array of objects.
    if (variables.product && Array.isArray(variables.product)) {
      variables.product = variables.product.map(p => {
      const [product, sector] = p.split('-').map(s => s.trim());
      return { product, sector };
      });
    }
    // Set query variables with ONLY supported GraphQL parameters
    const queryVariables = {
      limit: queryLimit,
      continuationToken: null,  // Will be updated during pagination
      product: variables.product || [],
      country: variables.country || [],
      subregion: variables.subregion || [],
      activity: variables.activity || []
    };
    
    perfLog(`Using ${queryVariables.limit} items per page, will fetch up to ${maxPages} pages`);
    
    console.log('AA Final Query Variables:', queryVariables);
    console.log('AA Using query:', QUERIES.PRODUCT_DATA);

    return this.executeQueryWithPagination(QUERIES.PRODUCT_DATA, queryVariables, {
      maxPages: maxPages,
      onPageReceived: (pageInfo) => {
        console.log(`AA Product data page ${pageInfo.pageNumber}: ${pageInfo.pageData.length} items (Total: ${pageInfo.totalItemsSoFar})`);
        console.log(`AA   -> Has more results: ${pageInfo.metaData.hasMoreResults}`);
        console.log(`AA   -> Continuation token: ${pageInfo.metaData.continuationToken ? 'Present' : 'None'}`);
      },
      ...options,
      token
    });
  },

  async getProductCounts(variables = {}) {
    try {
      const result = await this.executeQuery(QUERIES.PRODUCT_COUNTS, variables);

      console.log('Product counts result:', result);
      if (result.success && result.data && result.data.countByProductSector) {
        const keyedData = {};
        result.data.countByProductSector.forEach(item => {
          if (!keyedData[item.product]) {
            keyedData[item.product] = {};
          }
          keyedData[item.product][item.sector] = item.count;
        });
        return {
          success: true,
          data: keyedData,
        };
      } else {
        return {
          success: false,
          error: 'Failed to retrieve product counts',
        };
      }
    } catch (error) {
      console.error('GraphQL getProductCounts failed:', error);
      return {
        success: false,
        error: error.message,
        networkError: error.networkError?.message,
        graphQLErrors: error.graphQLErrors,
      };
    }
  },

  async getCountryCounts(variables = {}) {
    try {
      const result = await this.executeQuery(QUERIES.COUNTRY_COUNTS, variables);

      console.log('Country counts result:', result);
      if (result.success && result.data && result.data.countByCountry) {
        const keyedData = {};
        result.data.countByCountry.forEach(item => {
          keyedData[item.country] = item.count;
        });
        return {
          success: true,
          data: keyedData,
        };
      } else {
        return {
          success: false,
          error: 'Failed to retrieve country counts',
        };
      }
    } catch (error) {
      console.error('GraphQL getCountryCounts failed:', error);
      return {
        success: false,
        error: error.message,
        networkError: error.networkError?.message,
        graphQLErrors: error.graphQLErrors,
      };
    }
  },

  async getActivityCounts(variables = {}) {
    try {
      const result = await this.executeQuery(QUERIES.ACTIVITY_COUNTS, variables);

      console.log('Activity counts result:', result);
      if (result.success && result.data && result.data.countByActivity) {
        const keyedData = {};
        result.data.countByActivity.forEach(item => {
          keyedData[item.activity] = item.count;
        });
        return {
          success: true,
          data: keyedData,
        };
      } else {
        return {
          success: false,
          error: 'Failed to retrieve activity counts',
        };
      }
    } catch (error) {
      console.error('GraphQL getActivityCounts failed:', error);
      return {
        success: false,
        error: error.message,
        networkError: error.networkError?.message,
        graphQLErrors: error.graphQLErrors,
      };
    }
  },

  async getFilenameCounts() {
    // Implement as needed
    try {
      const result = await this.executeQuery(QUERIES.FILENAME_COUNTS);

      console.log('Filename counts result:', result);
      if (result.success && result.data && result.data.countByFilename) {
        return {
          success: true,
          data: result.data.countByFilename,
        };
      } else {
        return {
          success: false,
          error: 'Failed to retrieve filename counts',
        };
      }
    } catch (error) {
      console.error('GraphQL getFilenameCounts failed:', error);
      return {
        success: false,
        error: error.message,
        networkError: error.networkError?.message,
        graphQLErrors: error.graphQLErrors,
      };
    }

  }
};

export default client;

// USAGE EXAMPLES:
//
// 1. Test GraphQL API connection
// graphqlService.testConnection().then(result => {
//   if (result.success) {
//     console.log('Connection successful:', result.types, 'types found.');
//   } else {
//     console.error('Connection failed:', result.error);
//   }
// });
//
// 2. Execute a custom query
// graphqlService.executeQuery(QUERIES.GET_ENERGY_DATA).then(result => {
//   if (result.success) {
//     console.log('Energy data:', result.data);
//   } else {
//     console.error('Query failed:', result.error);
//   }
// });
//
// 3. Fetch all product data with pagination
// graphqlService.getAllProductData({ product: ['Oil'], country: ['USA'], activity: ['Production'] })
//   .then(result => {
//     if (result.success) {
//       console.log('All product data:', result.data.productData.data);
//     } else {
//       console.error('Pagination failed:', result.error);
//     }
//   });
//
// 4. Execute a mutation
// const MY_MUTATION = gql`mutation { ... }`;
// graphqlService.executeMutation(MY_MUTATION, { input: { ... } })
//   .then(result => {
//     if (result.success) {
//       console.log('Mutation result:', result.data);
//     } else {
//       console.error('Mutation failed:', result.error);
//     }
//   });
//
// 5. Clear Apollo cache
// graphqlService.clearCache().then(() => {
//   console.log('Cache cleared.');
// });
//
// 6. Get current cache contents
// const cacheContents = graphqlService.getCacheSize();
// console.log('Cache contents:', cacheContents);